import type { SessionSnapshotVersion } from "./session-snapshot";
import { STORAGE_KEYS } from "./storage";
import type { AuthResult, StorageAdapter } from "./types";

const LEASE_DURATION_MS = 15_000;
const LEASE_RENEW_INTERVAL_MS = 5_000;
const FOLLOWER_POLL_INTERVAL_MS = 50;
const FOLLOWER_WAIT_TIMEOUT_MS = 20_000;

interface RefreshLease {
  owner: string;
  epoch: string;
  revision: number;
  expiresAt: number;
  phase: "acquired" | "refreshing";
}

interface IndexedRefreshLease extends RefreshLease {
  lockName: string;
}

export class RefreshOutcomeIndeterminateError extends Error {
  constructor() {
    super("Refresh outcome is indeterminate; reauthentication is required");
    this.name = "RefreshOutcomeIndeterminateError";
  }
}

/** Coordinates refresh rotation so only one browser tab consumes a refresh token. */
export class RefreshCoordinator {
  constructor(
    private readonly storage: StorageAdapter,
    private readonly lockName: string,
    private readonly owner: string,
  ) {}

  /** Remove only a single-context lease belonging to the session being cleared. */
  clear(epoch: string): void {
    const current = parseLease(this.storage.getItem(STORAGE_KEYS.refreshLease));
    if (current?.epoch === epoch) this.storage.removeItem(STORAGE_KEYS.refreshLease);
  }

  async run(
    expected: SessionSnapshotVersion,
    readUpdated: () => AuthResult | null,
    refresh: () => Promise<AuthResult>,
    commit: (result: AuthResult) => AuthResult,
  ): Promise<AuthResult> {
    if (this.storage.sharing === "same-origin") {
      if (typeof indexedDB === "undefined") {
        throw new Error("Cross-tab refresh requires IndexedDB");
      }
      return this.runWithIndexedDbLease(expected, readUpdated, refresh, commit);
    }
    return this.runWithStorageLease(expected, readUpdated, refresh, commit);
  }

  private async runWithIndexedDbLease(
    expected: SessionSnapshotVersion,
    readUpdated: () => AuthResult | null,
    refresh: () => Promise<AuthResult>,
    commit: (result: AuthResult) => AuthResult,
  ): Promise<AuthResult> {
    const database = await openLeaseDatabase();
    const deadline = Date.now() + FOLLOWER_WAIT_TIMEOUT_MS;
    try {
      while (Date.now() < deadline) {
        const updated = readUpdated();
        if (updated) return updated;
        const acquisition = await this.tryAcquireIndexedLease(database, expected);
        if (acquisition === "indeterminate") throw new RefreshOutcomeIndeterminateError();
        if (acquisition === "acquired") {
          let renewalError: unknown = null;
          let renewalInFlight: Promise<void> | null = null;
          let enteredRefreshing = false;
          let completed = false;
          const renewal = setInterval(() => {
            if (renewalInFlight || renewalError) return;
            renewalInFlight = this.renewIndexedLease(database, expected)
              .catch((error: unknown) => {
                renewalError = error;
              })
              .finally(() => {
                renewalInFlight = null;
              });
          }, LEASE_RENEW_INTERVAL_MS);
          try {
            const updatedBeforeRefresh = readUpdated();
            if (updatedBeforeRefresh) return updatedBeforeRefresh;
            if (!(await this.markIndexedLeaseRefreshing(database, expected))) {
              throw new Error("Cross-tab refresh lease was lost");
            }
            enteredRefreshing = true;
            const result = await refresh();
            if (renewalInFlight) await renewalInFlight;
            if (renewalError) throw new Error("Cross-tab refresh lease renewal failed");
            if (!(await this.ownsIndexedLease(database, expected))) {
              const updatedAfterLeaseLoss = readUpdated();
              if (updatedAfterLeaseLoss) return updatedAfterLeaseLoss;
              throw new Error("Cross-tab refresh lease was lost");
            }
            const committed = commit(result);
            completed = true;
            return committed;
          } finally {
            clearInterval(renewal);
            if (renewalInFlight) await renewalInFlight;
            if (!enteredRefreshing || completed) await this.releaseIndexedLease(database, expected);
          }
        }
        await delay(FOLLOWER_POLL_INTERVAL_MS);
      }
      throw new Error(`Timed out waiting for cross-tab refresh at revision ${expected.revision}`);
    } finally {
      database.close();
    }
  }

  private async runWithStorageLease(
    expected: SessionSnapshotVersion,
    readUpdated: () => AuthResult | null,
    refresh: () => Promise<AuthResult>,
    commit: (result: AuthResult) => AuthResult,
  ): Promise<AuthResult> {
    const deadline = Date.now() + FOLLOWER_WAIT_TIMEOUT_MS;
    while (Date.now() < deadline) {
      const updated = readUpdated();
      if (updated) return updated;
      const acquisition = this.tryAcquireLease(expected);
      if (acquisition === "indeterminate") throw new RefreshOutcomeIndeterminateError();
      if (acquisition === "acquired") {
        let enteredRefreshing = false;
        let completed = false;
        const renewal = setInterval(() => this.renewLease(expected), LEASE_RENEW_INTERVAL_MS);
        try {
          const updatedBeforeRefresh = readUpdated();
          if (updatedBeforeRefresh) return updatedBeforeRefresh;
          if (!this.markLeaseRefreshing(expected)) throw new Error("Refresh lease was lost");
          enteredRefreshing = true;
          const result = await refresh();
          if (!this.ownsLease(expected)) {
            const updatedAfterLeaseLoss = readUpdated();
            if (updatedAfterLeaseLoss) return updatedAfterLeaseLoss;
            throw new Error("Refresh lease was lost");
          }
          const committed = commit(result);
          completed = true;
          return committed;
        } finally {
          clearInterval(renewal);
          if (!enteredRefreshing || completed) this.releaseLease(expected);
        }
      }
      await delay(FOLLOWER_POLL_INTERVAL_MS);
    }
    throw new Error(`Timed out waiting for cross-tab refresh at revision ${expected.revision}`);
  }

  private tryAcquireLease(expected: SessionSnapshotVersion): "acquired" | "busy" | "indeterminate" {
    const now = Date.now();
    const current = parseLease(this.storage.getItem(STORAGE_KEYS.refreshLease));
    if (current && current.epoch === expected.epoch && current.revision === expected.revision) {
      if (current.phase === "refreshing") return current.expiresAt > now ? "busy" : "indeterminate";
      if (current.owner !== this.owner && current.expiresAt > now) return "busy";
    }
    if (current && current.epoch === expected.epoch && current.revision > expected.revision) {
      return "indeterminate";
    }

    const candidate: RefreshLease = {
      owner: this.owner,
      epoch: expected.epoch,
      revision: expected.revision,
      expiresAt: now + LEASE_DURATION_MS,
      phase: "acquired",
    };
    this.storage.setItem(STORAGE_KEYS.refreshLease, JSON.stringify(candidate));
    const confirmed = parseLease(this.storage.getItem(STORAGE_KEYS.refreshLease));
    return leaseMatches(confirmed, this.owner, expected, "acquired") ? "acquired" : "busy";
  }

  private markLeaseRefreshing(expected: SessionSnapshotVersion): boolean {
    const current = parseLease(this.storage.getItem(STORAGE_KEYS.refreshLease));
    if (!leaseMatches(current, this.owner, expected, "acquired")) return false;
    this.storage.setItem(
      STORAGE_KEYS.refreshLease,
      JSON.stringify({ ...current, phase: "refreshing", expiresAt: Date.now() + LEASE_DURATION_MS }),
    );
    return leaseMatches(
      parseLease(this.storage.getItem(STORAGE_KEYS.refreshLease)),
      this.owner,
      expected,
      "refreshing",
    );
  }

  private renewLease(expected: SessionSnapshotVersion): void {
    const current = parseLease(this.storage.getItem(STORAGE_KEYS.refreshLease));
    if (!leaseMatches(current, this.owner, expected, "refreshing")) return;
    this.storage.setItem(
      STORAGE_KEYS.refreshLease,
      JSON.stringify({ ...current, expiresAt: Date.now() + LEASE_DURATION_MS }),
    );
  }

  private ownsLease(expected: SessionSnapshotVersion): boolean {
    const current = parseLease(this.storage.getItem(STORAGE_KEYS.refreshLease));
    return leaseMatches(current, this.owner, expected, "refreshing") && current.expiresAt > Date.now();
  }

  private releaseLease(expected: SessionSnapshotVersion): void {
    const current = parseLease(this.storage.getItem(STORAGE_KEYS.refreshLease));
    if (leaseMatches(current, this.owner, expected)) this.storage.removeItem(STORAGE_KEYS.refreshLease);
  }

  private async tryAcquireIndexedLease(
    database: IDBDatabase,
    expected: SessionSnapshotVersion,
  ): Promise<"acquired" | "busy" | "indeterminate"> {
    const transaction = database.transaction(INDEXED_DB_STORE, "readwrite");
    const store = transaction.objectStore(INDEXED_DB_STORE);
    const current = await requestResult<IndexedRefreshLease | undefined>(store.get(this.lockName));
    const now = Date.now();
    if (current && current.epoch === expected.epoch && current.revision === expected.revision) {
      if (current.phase === "refreshing") {
        await transactionDone(transaction);
        return "indeterminate";
      }
      if (current.owner !== this.owner && current.expiresAt > now) {
        await transactionDone(transaction);
        return "busy";
      }
    }
    if (current && current.epoch === expected.epoch && current.revision > expected.revision) {
      await transactionDone(transaction);
      return "indeterminate";
    }
    store.put({
      lockName: this.lockName,
      owner: this.owner,
      epoch: expected.epoch,
      revision: expected.revision,
      expiresAt: now + LEASE_DURATION_MS,
      phase: "acquired",
    } satisfies IndexedRefreshLease);
    await transactionDone(transaction);
    return "acquired";
  }

  private async markIndexedLeaseRefreshing(database: IDBDatabase, expected: SessionSnapshotVersion): Promise<boolean> {
    const transaction = database.transaction(INDEXED_DB_STORE, "readwrite");
    const store = transaction.objectStore(INDEXED_DB_STORE);
    const current = await requestResult<IndexedRefreshLease | undefined>(store.get(this.lockName));
    const owned = leaseMatches(current ?? null, this.owner, expected, "acquired");
    if (owned && current) {
      store.put({ ...current, phase: "refreshing", expiresAt: Date.now() + LEASE_DURATION_MS });
    }
    await transactionDone(transaction);
    return owned;
  }

  private async renewIndexedLease(database: IDBDatabase, expected: SessionSnapshotVersion): Promise<void> {
    const transaction = database.transaction(INDEXED_DB_STORE, "readwrite");
    const store = transaction.objectStore(INDEXED_DB_STORE);
    const current = await requestResult<IndexedRefreshLease | undefined>(store.get(this.lockName));
    if (leaseMatches(current ?? null, this.owner, expected, "refreshing")) {
      store.put({ ...current, expiresAt: Date.now() + LEASE_DURATION_MS });
    }
    await transactionDone(transaction);
  }

  private async ownsIndexedLease(database: IDBDatabase, expected: SessionSnapshotVersion): Promise<boolean> {
    const transaction = database.transaction(INDEXED_DB_STORE, "readonly");
    const current = await requestResult<IndexedRefreshLease | undefined>(
      transaction.objectStore(INDEXED_DB_STORE).get(this.lockName),
    );
    await transactionDone(transaction);
    const lease = current ?? null;
    return leaseMatches(lease, this.owner, expected, "refreshing") && lease.expiresAt > Date.now();
  }

  private async releaseIndexedLease(database: IDBDatabase, expected: SessionSnapshotVersion): Promise<void> {
    const transaction = database.transaction(INDEXED_DB_STORE, "readwrite");
    const store = transaction.objectStore(INDEXED_DB_STORE);
    const current = await requestResult<IndexedRefreshLease | undefined>(store.get(this.lockName));
    if (leaseMatches(current ?? null, this.owner, expected)) store.delete(this.lockName);
    await transactionDone(transaction);
  }
}

const INDEXED_DB_NAME = "zero-auth-refresh";
const INDEXED_DB_STORE = "leases";

function openLeaseDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(INDEXED_DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(INDEXED_DB_STORE)) {
        request.result.createObjectStore(INDEXED_DB_STORE, { keyPath: "lockName" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Failed to open refresh lease database"));
    request.onblocked = () => reject(new Error("Refresh lease database upgrade is blocked"));
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Refresh lease database request failed"));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error("Refresh lease transaction aborted"));
    transaction.onerror = () => reject(transaction.error ?? new Error("Refresh lease transaction failed"));
  });
}

function parseLease(encoded: string | null): RefreshLease | null {
  if (encoded === null || new TextEncoder().encode(encoded).length > 512) return null;
  try {
    const raw = JSON.parse(encoded) as Record<string, unknown>;
    if (
      typeof raw.owner !== "string" ||
      raw.owner.length === 0 ||
      raw.owner.length > 128 ||
      typeof raw.epoch !== "string" ||
      raw.epoch.length === 0 ||
      raw.epoch.length > 128 ||
      !/^[A-Za-z0-9_-]+$/.test(raw.epoch) ||
      !Number.isSafeInteger(raw.revision) ||
      typeof raw.revision !== "number" ||
      raw.revision <= 0 ||
      !Number.isSafeInteger(raw.expiresAt) ||
      typeof raw.expiresAt !== "number" ||
      raw.expiresAt <= 0 ||
      (raw.phase !== "acquired" && raw.phase !== "refreshing")
    ) {
      return null;
    }
    return {
      owner: raw.owner,
      epoch: raw.epoch,
      revision: raw.revision,
      expiresAt: raw.expiresAt,
      phase: raw.phase,
    };
  } catch {
    return null;
  }
}

function leaseMatches(
  lease: RefreshLease | null,
  owner: string,
  expected: SessionSnapshotVersion,
  phase?: RefreshLease["phase"],
): lease is RefreshLease {
  return (
    lease !== null &&
    lease.owner === owner &&
    lease.epoch === expected.epoch &&
    lease.revision === expected.revision &&
    (phase === undefined || lease.phase === phase)
  );
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}
